import { Badge, Button, Table } from "@jhool-io/fe-components";
import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
    cn,
    convertTimeTo12HoursFormat,
    displayNameInRightFormat,
    formatDate,
    handleDisplayClientEmailCorrectly,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../../../utils/helpers";
import {
    IProviderCaseloadDashboard,
    ISuperviseeCaseload,
} from "../../../../../providers/types/providers.types";
import ListState from "../../../../../../components/ListState/ListState";

interface SuperviseeCaseloadItemProps {
    isOpen?: boolean;
    details: ISuperviseeCaseload;
}

const columns: ColumnDef<IProviderCaseloadDashboard>[] = [
    {
        accessorKey: "client",
        header: "CLIENT NAME",
        cell: ({ row }) =>
            displayNameInRightFormat({
                firstName: row.original.first_name,
                lastName: row.original.last_name,
            }),
    },

    {
        accessorKey: "email",
        header: "EMAIL",
        cell: ({ row }) =>
            handleDisplayClientEmailCorrectly(row.original.email),
    },
    {
        accessorKey: "client_id",
        header: "SESSION TYPE",
        cell: () => "Teletherapy",
    },
    {
        accessorKey: "session_type",
        header: "APPOINTMENT",
        cell: ({ row }) =>
            makeStringFirstLetterCapital(
                removeEnumUnderscore(row.original.session_type)
            ),
    },
    {
        accessorKey: "recurrence_rule",
        header: "APPOINTMENT",
        cell: ({ row }) =>
            `${row.original.session_day}, ${convertTimeTo12HoursFormat(
                `${row.original.session_start_hr}:${row.original.session_start_min}`
            )} ${makeStringFirstLetterCapital(row.original.recurrence_rule)}`,
    },
    {
        accessorKey: "client_id",
        header: "starting on",
        cell: ({ row }) =>
            row.original.first_session_date
                ? formatDate(row.original.first_session_date)
                : "--",
    },
    {
        accessorKey: "",
        header: "status",
        cell: ({ row }) => (
            <div className="flex item-center gap-x-8">
                {row.original.accepts_assignment ? (
                    <Badge
                        content="Accepting A"
                        bgColor="#C6ECFF"
                        textColor="#205773"
                    />
                ) : null}
                {row.original.pending_supervisor_intake_note_task ? (
                    <Badge
                        content="Intake note"
                        bgColor="#D3D5D9"
                        textColor="#33363B"
                    />
                ) : (
                    <Badge
                        content="No Intake note"
                        bgColor="#FEC1C6"
                        textColor="#711920"
                    />
                )}
            </div>
        ),
    },
];

export default function SuperviseeCaseloadItem({
    isOpen,
    details,
}: SuperviseeCaseloadItemProps) {
    const [innerIsOpen, setInnerIsOpen] = useState(() => isOpen || false);

    const formattedData: IProviderCaseloadDashboard[] = [];

    details.client_appointments?.forEach((item) => {
        const { client } = item;
        item.appointments.forEach((innerItem) => {
            formattedData.push({
                ...client,
                ...innerItem,
            });
        });
    });

    return (
        <div className="rounded-r8 border border-strokedark bg-[--color-blue-100] text-black">
            <Button
                variant="normal"
                size="auto"
                className={cn(
                    "w-full flex items-center justify-between px-16 py-12 gap-x-8 transition-all hover:bg-[rgba(232,241,244,0.9)]",
                    {
                        "[&>svg]:rotate-180": innerIsOpen,
                    }
                )}
                onClick={() => setInnerIsOpen(!innerIsOpen)}
            >
                <div className="flex items-start flex-1 flex-wrap gap-y-16 gap-x-12 lg:gap-x-0">
                    <div className="flex flex-col gap-y-[2px] text-left lg:mr-12">
                        <span className="text-[--color-strong-950] text-sm font-medium leading-8 tracking-[-0.084px]">
                            {displayNameInRightFormat({
                                firstName: details.first_name,
                                lastName: details.last_name,
                            })}
                        </span>
                        <span className="text-[--color-sub-600] text-xs font-normal leading-16">
                            {details.email}
                        </span>
                    </div>
                    <span className="text-[--color-sub-600] text-xs uppercase font-normal">
                        {details.credential || "--"}
                    </span>
                    <div className="lg:mr-24 lg:ml-12">
                        <span className="text-xs font-medium leading-16 inline-flex gap-x-[2px]">
                            PHONE NO:
                            <span className="text-[--color-sub-600] font-normal">
                                {details.contact}
                            </span>
                        </span>
                    </div>
                    <Badge
                        content={`${details.active_clients} Clients`}
                        bgColor="#B8D4DC"
                        textColor="#0C343F"
                    />
                    <Badge
                        content={`${details.pending_notes} Pending Notes`}
                        bgColor="#FCE8B9"
                        textColor="#6F510D"
                        className="lg:ml-12"
                    />
                </div>
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="shrink-0 transition-transform duration-200"
                >
                    <path
                        d="M11.6693 4.66406L7.0026 9.33073L2.33594 4.66406"
                        stroke="black"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Button>
            {innerIsOpen ? (
                <div
                    className={cn(
                        "overflow-hidden transition-all mx-4 mb-[9px] bg-white",
                        {
                            "animate-accordion-down": innerIsOpen,
                            "animate-accordion-up": !innerIsOpen,
                        }
                    )}
                >
                    {details.client_appointments.length === 0 ? (
                        <ListState
                            stateHelperText="No caseload to display"
                            isError={false}
                            emptyMessage="No caseload"
                            context="general"
                            cardClass="h-[250px]"
                        />
                    ) : null}
                    {formattedData.length > 0 ? (
                        <div>
                            <Table columns={columns} data={formattedData} />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
